import { useCallback, type ReactNode } from 'react';
import { Button } from '@viquard/doc-ui/components';
import { IcnDownload } from '@viquard/doc-ui/icons';
import type { CalendarEvent } from '../types.js';
import { useCalendarMessages } from '../i18n.js';
import { downloadICS, eventsToICS } from '../utils/ics.js';

export interface ICSExportButtonProps {
  readonly events: readonly CalendarEvent[];
  readonly filename?: string | undefined;
  readonly calendarName?: string | undefined;
  readonly label?: string | undefined;
  readonly variant?: 'primary' | 'secondary' | 'ghost' | undefined;
  readonly size?: 'sm' | 'md' | undefined;
}

/**
 * Exports the given events as an `.ics` file. plan.md XIX.3 — every
 * calendar viewer can download or subscribe; subscribe (`webcal://`) is
 * a separate consumer-supplied URL.
 */
export function ICSExportButton(props: ICSExportButtonProps): ReactNode {
  const messages = useCalendarMessages();
  const {
    events,
    filename = 'calendar.ics',
    calendarName = 'Calendar',
    label = messages.icsExport,
    variant = 'ghost',
    size = 'sm',
  } = props;

  const onClick = useCallback(() => {
    const ics = eventsToICS(events, { calendarName });
    downloadICS(filename, ics);
  }, [events, calendarName, filename]);

  return (
    <Button
      variant={variant}
      size={size}
      iconLeft={<IcnDownload size={size === 'sm' ? 13 : 15} />}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
